import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { collection, doc, query, orderBy, limit, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { EmptyState, LoadingSkeleton, Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { formatRelativeTime } from '@/lib/formatRelative';
import {
  pushPermissionState,
  requestPushPermissionAndRegister,
} from '@/lib/pushNotifications';
import { isIosDevice, isStandaloneApp } from '@/lib/pwaInstall';
import type { NotificationDoc } from '@makhe/shared';

const TYPE_LABEL: Record<string, string> = {
  booking_requested: 'Booking',
  booking_accepted: 'Booking',
  booking_declined: 'Booking',
  booking_counter_proposed: 'New time',
  booking_cancelled: 'Booking',
  booking_reminder: 'Reminder',
  booking_reminder_1h: 'Reminder',
  payment_received: 'Payment',
  payment_released: 'Payment',
  payout_sent: 'Payment',
  review_received: 'Review',
  review_longevity: 'Review',
  verification_approved: 'Verified',
  verification_rejected: 'Verification',
  request_new: 'New request',
  request_offer: 'Offer',
  request_accepted: 'Offer accepted',
  request_declined: 'Request',
  request_cancelled: 'Request',
  request_expired: 'Request',
  admin_announcement: 'Makhe',
  welcome: 'Welcome',
};

function typeLabel(type: string): string {
  return TYPE_LABEL[type] ?? type.replace(/_/g, ' ');
}

export default function NotificationsPage() {
  const { firebaseUser } = useAuth();
  const [enablingPush, setEnablingPush] = useState(false);
  const [permission, setPermission] = useState(pushPermissionState);
  const iosNeedsInstall = isIosDevice() && !isStandaloneApp();
  const showPushCta =
    permission === 'default' ||
    permission === 'denied' ||
    (iosNeedsInstall && permission !== 'granted');

  useEffect(() => {
    setPermission(pushPermissionState());
  }, []);

  const q = useMemo(() => {
    if (!firebaseUser) return null;
    return query(
      collection(db, 'users', firebaseUser.uid, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
  }, [firebaseUser]);
  const { data: notes, loading } = useCollection<NotificationDoc>(q);

  const freshAtMount = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (loading || !firebaseUser) return;
    if (!freshAtMount.current) {
      freshAtMount.current = new Set(notes.filter((n) => !n.isRead).map((n) => n.id));
    }
    notes
      .filter((n) => !n.isRead)
      .forEach((n) =>
        updateDoc(doc(db, 'users', firebaseUser.uid, 'notifications', n.id), { isRead: true }).catch(
          () => undefined,
        ),
      );
  }, [notes, loading, firebaseUser]);

  const markRead = async (id: string) => {
    if (!firebaseUser) return;
    await updateDoc(doc(db, 'users', firebaseUser.uid, 'notifications', id), { isRead: true });
  };

  const enablePush = async () => {
    setEnablingPush(true);
    try {
      const result = await requestPushPermissionAndRegister();
      setPermission(pushPermissionState());
      if (result === 'granted') toast.success('Push notifications on.');
      else if (result === 'denied') {
        toast.error('Permission denied — enable notifications in your browser settings.');
      } else {
        toast.error('Push isn’t available in this browser.');
      }
    } finally {
      setEnablingPush(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-5 py-6 pb-10 md:px-6">
      <div className="border-b border-makhe-line pb-4">
        <p className="eyebrow mb-2">Updates</p>
        <h1
          className="font-display text-3xl leading-none md:text-4xl"
          style={{ fontVariationSettings: '"opsz" 72, "wght" 450' }}
        >
          Inbox
        </h1>
      </div>

      {showPushCta && (
        <Card className="mt-4 space-y-2">
          {iosNeedsInstall ? (
            <p className="text-sm text-makhe-muted">
              On iPhone, add Makhe to your Home Screen, then open it from there to turn on push
              alerts.
            </p>
          ) : permission === 'denied' ? (
            <p className="text-sm text-makhe-muted">
              Notifications are blocked for this site. Unblock them in your browser settings, then
              tap below.
            </p>
          ) : (
            <p className="text-sm text-makhe-muted">
              Turn on push so you get booking updates when Makhe isn&apos;t open.
            </p>
          )}
          {!iosNeedsInstall && (
            <Button onClick={() => void enablePush()} loading={enablingPush} className="w-full">
              Turn on notifications
            </Button>
          )}
        </Card>
      )}
      <div className="mt-6 space-y-2">
        {loading && (
          <>
            <LoadingSkeleton className="h-24" />
            <LoadingSkeleton className="h-24" />
          </>
        )}
        {!loading && notes.length === 0 && (
          <EmptyState
            title="You're all caught up"
            description="Booking updates, offers, and verification news land here."
          />
        )}
        {notes.map((n) => {
          const isFresh = !!freshAtMount.current?.has(n.id);
          const when = formatRelativeTime(n.createdAt?.toDate?.());
          return (
            <Link
              key={n.id}
              to={n.deepLink}
              onClick={() => !n.isRead && markRead(n.id)}
              className={`group flex items-start gap-3 rounded-lg border border-makhe-line p-4 transition-shadow hover:shadow-lift ${
                isFresh ? 'bg-makhe-paper shadow-soft' : 'bg-makhe-paper/50 opacity-80'
              }`}
            >
              <span
                aria-hidden
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                  isFresh ? 'bg-makhe-primary' : 'bg-transparent'
                }`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-[10px] uppercase tracking-widest text-makhe-muted">
                    {typeLabel(n.type)}
                    {when ? ` · ${when}` : ''}
                  </p>
                  <ArrowRight
                    size={14}
                    className="shrink-0 text-makhe-muted transition-transform group-hover:translate-x-0.5"
                  />
                </div>
                <p className="mt-1 font-display text-lg leading-tight">{n.title}</p>
                <p className="mt-1 text-sm leading-snug text-makhe-ink/70">{n.body}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
